import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Image, StyleSheet, ScrollView } from 'react-native';
import {AwardIcon} from './AwardIcon';
import StarAsset from '@/Assets/Recognition/Star.svg';
import FlashAsset from '@/Assets/Recognition/Flash.svg';
import StreakAsset from '@/Assets/Recognition/Streak.svg';
import CrownAsset from '@/Assets/Recognition/Crown.svg';

const C = {
  bg: '#FAF6EB', card: '#FFFFFF', brown: '#4D3F37',
  brownLight: '#4D3F37CC', gold: '#FACF47', goldLight: '#FEF2B5',
  aqua: '#3DAB9E', coral: '#E17B21', purple: '#937ADB',
  border: 'rgba(38,35,34,0.18)',
};

const STREAK_TIERS = [
  { threshold: 7, name: 'LEGENDARY',   color: C.purple, asset: CrownAsset,  bg: '#F0EAFF' },
  { threshold: 5, name: 'Unstoppable', color: C.coral,  asset: FlashAsset,  bg: '#FFF0E8' },
  { threshold: 3, name: 'On a Roll',   color: C.gold,   asset: StreakAsset, bg: '#FFFBF0' },
];

const AWARDS = [
  { key: 'good_try',      label: 'Good Try',      shortLabel: 'Good Try',  color: C.aqua,   tint: '#21CDBD29' },
  { key: 'great_effort',  label: 'Great Effort',  shortLabel: 'Effort',    color: C.coral,  tint: '#FF854B29' },
  { key: 'good_thinking', label: 'Good Thinking', shortLabel: 'Thinking',  color: C.gold,   tint: C.goldLight },
  { key: 'focused',       label: 'Focused',       shortLabel: 'Focus',     color: C.purple, tint: '#937ADB26' },
  { key: 'team_player',   label: 'Team Player',   shortLabel: 'Team',      color: C.brown,  tint: '#4D3F3712' },
];

const EVENT_META: Record<string, { label: string; color: string }> = {
  joined_on_time:     { label: 'Joined on time',          color: C.aqua   },
  homework_complete:  { label: 'Homework completed',      color: C.aqua   },
  puzzle_attempt:     { label: 'Attempted puzzle',        color: C.brown  },
  correct_solve:      { label: 'Solved puzzle',           color: C.aqua   },
  fastest_finger:     { label: 'Flash Solve! Fastest',    color: C.coral  },
  good_try:           { label: 'Good Try from Coach',     color: C.aqua   },
  great_effort:       { label: 'Great Effort from Coach', color: C.coral  },
  good_thinking:      { label: 'Good Thinking from Coach',color: C.gold   },
  focused:            { label: 'Focused badge from Coach',color: C.purple },
  team_player:        { label: 'Team Player from Coach',  color: C.brown  },
  mystery_bonus:      { label: 'Mystery Bonus!',          color: C.purple },
};

function tierFor(streak: number) {
  return STREAK_TIERS.find(t => streak >= t.threshold) ?? null;
}

// ── StudentMiniBanner ─────────────────────────────────────────
interface StudentMiniBannerProps {
  stars: number;
  streak: number;
  awardsCount: number;
  onTap: () => void;
}

export function StudentMiniBanner({ stars, streak, awardsCount, onTap }: StudentMiniBannerProps) {
  const [collapsed, setCollapsed] = useState(false);
  const tier = tierFor(streak);

  if (collapsed) {
    return (
      <View style={banner.row}>
        <TouchableOpacity onPress={onTap} style={banner.collapsedBody}>
          <Image source={StarAsset} style={banner.starImg} resizeMode="contain" />
          <Text style={banner.starCount}>{stars}</Text>
          {tier && (
            <>
              <Text style={banner.dot}> · </Text>
              <Image source={tier.asset} style={banner.tierImg} resizeMode="contain" />
              <Text style={[banner.tierCount, { color: tier.color }]}>{streak}</Text>
            </>
          )}
          {awardsCount > 0 && (
            <>
              <Text style={banner.dot}> · </Text>
              <Text style={banner.awardsCount}>{awardsCount} {awardsCount === 1 ? 'award' : 'awards'}</Text>
            </>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => setCollapsed(false)}
          accessibilityLabel="Expand"
          style={banner.chevronBtn}
        >
          <Text style={banner.chevron}>▾</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={banner.row}>
      <TouchableOpacity onPress={onTap} style={banner.expandedBody}>
        <View style={banner.medalCircle}>
          <Image source={StarAsset} style={{ width: 18, height: 18 }} resizeMode="contain" />
        </View>
        <View style={banner.expandedMeta}>
          <Text style={banner.starCount}>{stars}</Text>
          <Text style={banner.starsLabel}> stars</Text>
        </View>
        {tier && (
          <>
            <Text style={banner.dot}> · </Text>
            <Image source={tier.asset} style={banner.tierImg} resizeMode="contain" />
            <Text style={[banner.tierName, { color: tier.color }]}>{tier.name}</Text>
          </>
        )}
        {awardsCount > 0 && (
          <>
            <Text style={banner.dot}> · </Text>
            <Text style={banner.awardsCount}>
              <Text style={banner.awardsCountNum}>{awardsCount}</Text> awards
            </Text>
          </>
        )}
        <View style={{ flex: 1 }} />
        <Text style={banner.viewLink}>View →</Text>
      </TouchableOpacity>
      <TouchableOpacity
        onPress={() => setCollapsed(true)}
        accessibilityLabel="Collapse"
        style={banner.chevronBtn}
      >
        <Text style={banner.chevron}>▴</Text>
      </TouchableOpacity>
    </View>
  );
}

const banner = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'stretch', backgroundColor: C.card, borderBottomWidth: 1, borderBottomColor: C.border },
  collapsedBody: { flex: 1, flexDirection: 'row', alignItems: 'center', padding: 5, paddingHorizontal: 12 },
  expandedBody: { flex: 1, flexDirection: 'row', alignItems: 'center', padding: 10, paddingHorizontal: 14 },
  chevronBtn: { width: 32, alignItems: 'center', justifyContent: 'center', borderLeftWidth: 1, borderLeftColor: C.border },
  chevron: { fontSize: 11, color: C.brownLight },
  medalCircle: { width: 28, height: 28, borderRadius: 14, backgroundColor: C.goldLight, borderWidth: 1, borderColor: C.gold, alignItems: 'center', justifyContent: 'center', marginRight: 8 },
  expandedMeta: { flexDirection: 'row', alignItems: 'baseline' },
  starIcon: { fontSize: 13 },
  starImg: { width: 16, height: 16 },
  tierImg: { width: 16, height: 16, marginLeft: 4 },
  starCount: { fontSize: 16, fontWeight: '800', color: C.brown, marginLeft: 4 },
  starsLabel: { fontSize: 11, fontWeight: '500', color: C.brownLight },
  tierName: { fontSize: 12, fontWeight: '700', marginLeft: 4 },
  tierCount: { fontSize: 11, fontWeight: '700', marginLeft: 2 },
  dot: { fontSize: 10, color: C.brownLight },
  awardsCount: { fontSize: 11, fontWeight: '600', color: C.brownLight },
  awardsCountNum: { fontWeight: '700', color: C.brown },
  viewLink: { fontSize: 11, fontWeight: '600', color: C.aqua },
});

// ── StudentStarsScreen ────────────────────────────────────────
interface StarEvent {
  event_type: string;
  stars_awarded: number;
  created_at: string;
  is_silent: boolean;
}

interface StudentStarsScreenProps {
  stars: number;
  streak: number;
  awards: string[];
  events: StarEvent[];
  rank: number;
  totalStudents: number;
  studentName: string;
}

export function StudentStarsScreen({
  stars, streak, awards, events, rank, totalStudents, studentName,
}: StudentStarsScreenProps) {
  const tier = tierFor(streak);
  const nextTierStreak = streak >= 7 ? null : streak >= 5 ? 7 : streak >= 3 ? 5 : 3;
  const nextTierName = nextTierStreak === 7 ? 'LEGENDARY' : nextTierStreak === 5 ? 'Unstoppable' : 'On a Roll';

  const greeting = stars >= 7 ? 'Brilliant class!' : stars >= 4 ? 'Great class!' : stars >= 1 ? 'Nice work today!' : "Let's earn some stars!";
  const earnedSet = new Set(awards);
  const allAwards = AWARDS.map(a => ({ ...a, earned: earnedSet.has(a.key) }));

  return (
    <ScrollView style={screen.container}>
      {/* Hero */}
      <View style={screen.hero}>
        <Text style={screen.greeting}>{greeting}</Text>
        <Text style={screen.studentName}>{studentName}</Text>
        <View style={screen.medallion}>
          <Text style={screen.medallionStars}>{stars}</Text>
          <View style={screen.medallionLabelRow}>
            <Image source={StarAsset} style={{ width: 14, height: 14 }} resizeMode="contain" />
            <Text style={screen.medallionLabel}> STARS</Text>
          </View>
        </View>
        <View style={screen.rankChip}>
          <Image source={CrownAsset} style={{ width: 16, height: 16 }} resizeMode="contain" />
          <Text style={screen.rankText}>
            <Text style={{ fontWeight: '800' }}>#{rank}</Text> of {totalStudents} in class today
          </Text>
        </View>
      </View>

      {/* Streak */}
      <View style={screen.section}>
        {tier ? (
          <View style={[screen.streakCard, { borderColor: tier.color + '55', backgroundColor: tier.bg }]}>
            <Text style={[screen.streakLabel, { color: tier.color }]}>STREAK</Text>
            <Text style={screen.streakName}>{tier.name}</Text>
            <Text style={screen.streakMeta}>
              <Text style={screen.streakStrong}>{streak} correct in a row</Text>
              {nextTierStreak != null ? ` · ${nextTierStreak - streak} more for ${nextTierName}` : ''}
            </Text>
          </View>
        ) : (
          <View style={screen.streakEmpty}>
            <Image source={StreakAsset} style={{ width: 26, height: 26, opacity: 0.55 }} resizeMode="contain" />
            <View>
              <Text style={screen.streakEmptyTitle}>Streak hasn't started</Text>
              <Text style={screen.streakEmptyBody}>Solve 3 puzzles in a row to start one</Text>
            </View>
          </View>
        )}
      </View>

      {/* Today's Journey */}
      {events.length > 0 && (
        <View style={screen.section}>
          <View style={screen.sectionHeader}>
            <Text style={screen.sectionTitle}>TODAY'S JOURNEY</Text>
            <Text style={screen.sectionMeta}>{events.length} moments</Text>
          </View>
          <View style={screen.journeyCard}>
            {events.map((ev, i) => {
              const meta = EVENT_META[ev.event_type] ?? { label: ev.event_type, color: C.gold };
              const isHighlight = ev.event_type === 'fastest_finger';
              const isLast = i === events.length - 1;
              return (
                <View key={i} style={[screen.journeyRow, !isLast && screen.journeyRowBorder]}>
                  <View style={[screen.journeyDot, { borderColor: meta.color }]}>
                    <AwardIcon awardKey={ev.event_type} size={18} color={meta.color} />
                  </View>
                  <View style={{ flex: 1, paddingTop: 4 }}>
                    <View style={screen.journeyRowHeader}>
                      <Text style={[screen.journeyLabel, isHighlight && { fontWeight: '800' }]}>{meta.label}</Text>
                      <View style={screen.journeyDeltaRow}>
                        <Text style={[screen.journeyDelta, { color: isHighlight ? meta.color : C.gold }]}>+{ev.stars_awarded}</Text>
                        <Image source={StarAsset} style={{ width: 12, height: 12, marginLeft: 2 }} resizeMode="contain" />
                      </View>
                    </View>
                    <Text style={screen.journeyTime}>{ev.created_at}</Text>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      )}

      {/* Coach Badges */}
      <View style={[screen.section, screen.sectionBottom]}>
        <View style={screen.sectionHeader}>
          <Text style={screen.sectionTitle}>COACH BADGES</Text>
          <Text style={screen.sectionMeta}>{awards.length} / {AWARDS.length} earned</Text>
        </View>
        <View style={screen.badgesCard}>
          <View style={screen.badgeGrid}>
            {allAwards.map(a => (
              <View key={a.key} style={[screen.badge, { opacity: a.earned ? 1 : 0.32 }]}>
                <View style={[screen.badgeCircle, { backgroundColor: a.earned ? a.tint : C.bg, borderColor: a.earned ? a.color : C.border }]}>
                  <AwardIcon awardKey={a.key} size={26} color={a.color} />
                </View>
                <Text style={[screen.badgeLabel, { color: a.earned ? C.brown : C.brownLight }]}>{a.shortLabel}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

const screen = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg },
  hero: { padding: 32, paddingTop: 24, alignItems: 'center', backgroundColor: C.goldLight },
  greeting: { fontSize: 13, fontWeight: '600', color: C.brown, opacity: 0.7 },
  studentName: { fontSize: 17, fontWeight: '800', color: C.brown, marginTop: 4 },
  medallion: { width: 120, height: 120, borderRadius: 60, backgroundColor: C.gold, alignItems: 'center', justifyContent: 'center', marginTop: 18, borderWidth: 3, borderColor: '#C19023' },
  medallionStars: { fontSize: 40, fontWeight: '800', color: C.brown, lineHeight: 44 },
  medallionLabel: { fontSize: 10, fontWeight: '800', color: C.brown, letterSpacing: 1.5 },
  medallionLabelRow: { flexDirection: 'row', alignItems: 'center', marginTop: 2 },
  journeyDeltaRow: { flexDirection: 'row', alignItems: 'center' },
  rankChip: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 18, paddingHorizontal: 14, paddingVertical: 6, backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: 999 },
  rankText: { fontSize: 12, fontWeight: '600', color: C.brown },
  section: { paddingHorizontal: 12, paddingTop: 16 },
  sectionBottom: { paddingBottom: 24 },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', paddingHorizontal: 6, marginBottom: 10 },
  sectionTitle: { fontSize: 11, fontWeight: '800', color: C.brown, letterSpacing: 1.5 },
  sectionMeta: { fontSize: 11, fontWeight: '600', color: C.brownLight },
  streakCard: { borderWidth: 1, borderRadius: 14, padding: 16 },
  streakLabel: { fontSize: 10, fontWeight: '800', letterSpacing: 1.5, textTransform: 'uppercase' },
  streakName: { fontSize: 22, fontWeight: '800', color: C.brown, marginTop: 2 },
  streakMeta: { fontSize: 13, fontWeight: '500', color: C.brownLight, marginTop: 2 },
  streakStrong: { color: C.brown, fontWeight: '700' },
  streakEmpty: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 16, backgroundColor: C.card, borderWidth: 1, borderStyle: 'dashed', borderColor: C.border, borderRadius: 14 },
  streakEmptyTitle: { fontSize: 14, fontWeight: '700', color: C.brown },
  streakEmptyBody: { fontSize: 11, color: C.brownLight, marginTop: 2 },
  journeyCard: { backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: 14, padding: 16, paddingHorizontal: 18 },
  journeyRow: { flexDirection: 'row', gap: 12, paddingBottom: 14 },
  journeyRowBorder: {},
  journeyDot: { width: 30, height: 30, borderRadius: 15, borderWidth: 2, alignItems: 'center', justifyContent: 'center' },
  journeyRowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' },
  journeyLabel: { fontSize: 13, fontWeight: '600', color: C.brown },
  journeyDelta: { fontSize: 12, fontWeight: '800' },
  journeyTime: { fontSize: 11, fontWeight: '500', color: C.brownLight, marginTop: 2 },
  badgesCard: { backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: 14, padding: 14 },
  badgeGrid: { flexDirection: 'row', justifyContent: 'space-around' },
  badge: { alignItems: 'center', gap: 6, width: 56 },
  badgeCircle: { width: 52, height: 52, borderRadius: 26, borderWidth: 2, alignItems: 'center', justifyContent: 'center' },
  badgeLabel: { fontSize: 10, fontWeight: '700', textAlign: 'center', lineHeight: 13 },
});
